// CB:BEGIN admin-milestone-management-production
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SharedHeaderAdmin from '../../components/SharedHeaderAdmin';
import SettingsCard from '../../components/ui/SettingsCard';
import { useApi } from '@/lib/useApi';

interface AdminMilestoneManagementProps {
  projectId: string;
  onNavigate?: (screen: string) => void;
}

interface EvidenceFile {
  key: string;
  url: string | null;
}

interface Evidence {
  id: string;
  status: 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';
  submittedAt: string;
  fileKeys: string[];
  fileUrls: EvidenceFile[];
}

interface MilestoneData {
  id: string;
  milestoneKey: string;
  status: string;
  amountCents: number;
  releasedAt?: string;
  contractorPayoutId?: string;
  homeownerApprovedAt?: string;
  evidence: Evidence[];
}

interface Milestone {
  id: string;
  number: number;
  name: string;
  status: 'PENDING' | 'CONTRACTOR_SUBMITTED' | 'HOMEOWNER_APPROVED' | 'RELEASED' | 'ADMIN_RELEASED' | 'COMPLETED';
  percent: number;
  amountCents: number;
  submittedAt?: string;
  approvedAt?: string;
  releasedAt?: string;
  metadata?: {
    stripeTransferId?: string;
  };
  milestone?: MilestoneData | null;
}

interface Project {
  id: string;
  address: string;
  status: string;
  escrowAccount?: {
    currentBalance: number;
    totalDeposited: number;
    totalReleased: number;
  };
}

const AdminMilestoneManagement: React.FC<AdminMilestoneManagementProps> = ({ projectId, onNavigate }) => {
  const api = useApi();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<Project | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [releasing, setReleasing] = useState<string | null>(null);
  const [refunding, setRefunding] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ 
    name: '', 
    percent: '', 
    amount: '' 
  });
  const [editingPhase, setEditingPhase] = useState<number | null>(null);
  const [editPhaseName, setEditPhaseName] = useState('');
  const [editPhaseAmount, setEditPhaseAmount] = useState('');
  const [approvingEvidence, setApprovingEvidence] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async (skipCache = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch project with escrow account
      const projectData = await api.get(`/api/admin/projects/${projectId}`, { dedupeKey: skipCache ? false : undefined });
      setProject(projectData);
      
      // Fetch milestones/phases - add cache buster if skipCache is true
      const phasesUrl = skipCache
        ? `/api/admin/projects/${projectId}/phases?t=${Date.now()}`
        : `/api/admin/projects/${projectId}/phases`;
      const milestonesData = await api.get(phasesUrl, { dedupeKey: skipCache ? false : undefined });
      const phases = milestonesData.phases || milestonesData;
      setMilestones(Array.isArray(phases) ? phases : []);
      // Merge escrowAccount from phases response (not included in GET /api/admin/projects/:id)
      if (milestonesData.escrowAccount) {
        setProject(prev => prev ? { ...prev, escrowAccount: milestonesData.escrowAccount } : prev);
      }
      
      // CB:BEGIN log-milestone-statuses
      console.log('📥 Fetched milestones:', phases);
      if (Array.isArray(phases)) {
        phases.forEach((phase: any) => {
          console.log(`  Phase ${phase.number}: status="${phase.status}", approvedAt="${phase.approvedAt}"`);
        });
      }
      // CB:END log-milestone-statuses
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMilestone = async () => {
    if (!newMilestone.name || !newMilestone.amount || !newMilestone.percent) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setError(null);
      const amount = parseFloat(newMilestone.amount) * 100;
      const percent = parseFloat(newMilestone.percent);
      
      await api.post(`/api/admin/projects/${projectId}/phases`, {
        name: newMilestone.name,
        percent: percent,
        amountCents: amount
      });
      
      setShowCreateModal(false);
      setNewMilestone({ name: '', percent: '', amount: '' });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
      fetchData();
    } catch (error) {
      console.error('Error creating milestone:', error);
      setError('Failed to create milestone');
    }
  };

  const handleReleaseMilestone = async (phaseNumber: number) => {
    // CB:BEGIN enhanced-release-confirmation
    const milestone = milestones.find(m => m.number === phaseNumber);
    const amount = milestone ? `$${(milestone.amountCents / 100).toFixed(2)}` : '';
    const statusWarning = milestone?.status === 'CONTRACTOR_SUBMITTED' 
      ? '\n\n⚠️ Note: Homeowner has not approved this milestone yet. You are releasing payment anyway.' 
      : '';
    
    if (!confirm(`Are you sure you want to release payment of ${amount} to the contractor?${statusWarning}\n\nThis will:\n- Send the payment via Stripe\n- Update the milestone status\n- Deduct from escrow balance`)) {
      return;
    }
    // CB:END enhanced-release-confirmation

    try {
      setReleasing(`${phaseNumber}`);
      setError(null);
      
      console.log(`💰 Releasing milestone ${phaseNumber}...`);
      const response = await api.post(`/api/admin/projects/${projectId}/phases/${phaseNumber}/release`);
      
      console.log('✅ Release response:', response);
      
      if (response.stripeTransferId) {
        console.log(`✅ Stripe Transfer ID: ${response.stripeTransferId}`);
        alert(`✅ Payment released successfully!\n\nAmount: ${response.amountFormatted || amount}\nStripe Transfer ID: ${response.stripeTransferId}\n\nYou can view this transfer in your Stripe dashboard.`);
      } else {
        alert(`✅ Payment released successfully! Amount: ${response.amountFormatted || amount}`);
      }
      
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchData(true); // Refresh with cache bypass
      }, 2000);
    } catch (error: any) {
      console.error('❌ Error releasing milestone:', error);
      const errorMessage = String(error?.response?.data?.error || error?.message || 'Failed to release payment');
      const errorDetails = error?.response?.data?.details || '';
      const stripeError = error?.response?.data?.stripeError || '';

      let fullErrorMessage = errorMessage;
      if (errorDetails) fullErrorMessage += `\n\nDetails: ${errorDetails}`;
      if (stripeError) fullErrorMessage += `\n\nStripe Error: ${stripeError}`;

      setError(errorMessage);
      alert(`❌ Failed to release payment:\n\n${fullErrorMessage}`);
    } finally {
      setReleasing(null);
    }
  };

  const handleApproveEvidence = async (phaseNumber: number, approved: boolean) => {
    const action = approved ? 'approve' : 'reject';
    if (!confirm(`Are you sure you want to ${action} this evidence?\n\n${approved ? 'This will approve the evidence and release the payment to the contractor.' : 'This will reject the evidence. The contractor will need to resubmit.'}`)) return;
    try {
      setApprovingEvidence(`${phaseNumber}`);
      setError(null);
      const isTestMode = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_') ?? true;
      const modeNote = isTestMode ? '\n\n[TEST MODE - No real payment was processed]' : '\n\n[LIVE PAYMENT - Funds transferred to contractor]';
      const response = await api.post(`/api/admin/projects/${projectId}/phases/${phaseNumber}/approve-evidence`, { approved });
      if (approved) {
        if (response.released && response.releaseResult?.stripeTransferId) {
          alert(`Evidence approved and payment released.\n\nAmount: ${response.releaseResult.amountFormatted}\nStripe Transfer ID: ${response.releaseResult.stripeTransferId}${modeNote}`);
        } else if (response.released) {
          alert(`Evidence approved and payment of ${response.releaseResult?.amountFormatted} released.${modeNote}`);
        } else if (response.releaseError) {
          alert(`Evidence approved.\n\nAuto-release failed: ${response.releaseError}\n\nUse the "Release Payment" button to retry.`);
        }
      }
      setSuccess(true);
      setTimeout(() => { setSuccess(false); fetchData(true); }, 1500);
    } catch (err: any) {
      setError(String(err?.response?.data?.error || err?.message || 'Failed to process evidence'));
    } finally {
      setApprovingEvidence(null);
    }
  };

  const handleEditPhase = (milestone: Milestone) => {
    if (!confirm(`Edit "${milestone.name}"?\n\nYou can change the phase name and amount. This won't affect any already-released payments.`)) return;
    setEditingPhase(milestone.number);
    setEditPhaseName(milestone.name);
    setEditPhaseAmount((milestone.amountCents / 100).toFixed(2));
  };

  const handleSavePhase = async (phaseNumber: number) => {
    try {
      setError(null);
      
      // Validate input
      const amountValue = parseFloat(editPhaseAmount);
      if (isNaN(amountValue) || amountValue <= 0) {
        setError('Please enter a valid amount greater than 0');
        return;
      }
      
      const amountCents = Math.round(amountValue * 100);
      console.log('💾 Saving milestone:', { phaseNumber, amountCents, editPhaseAmount, amountValue });
      
      const response = await api.put(`/api/admin/projects/${projectId}/phases/${phaseNumber}`, {
        name: editPhaseName,
        amountCents: amountCents
      });
      
      console.log('✅ Save response:', response);
      
      setEditingPhase(null);
      setEditPhaseName('');
      setEditPhaseAmount('');
      setSuccess(true);
      
      // Refresh data immediately with cache bypass to show updated values
      await fetchData(true);
      
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error('❌ Error updating phase:', error);
      setError(String(error?.response?.data?.error || error?.message || 'Failed to update phase'));
    }
  };

  const handleDeletePhase = async (phaseNumber: number, phaseName: string) => {
    if (!confirm(`Are you sure you want to delete "${phaseName}"? This action cannot be undone and will renumber remaining phases.`)) {
      return;
    }

    try {
      setError(null);
      
      await api.del(`/api/admin/projects/${projectId}/phases/${phaseNumber}`);
      
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchData();
      }, 2000);
    } catch (error: any) {
      console.error('Error deleting phase:', error);
      setError(String(error?.response?.data?.error || 'Failed to delete phase'));
    }
  };

  const handleCompleteProject = async () => {
    if (!confirm('Mark this project as complete? This will close the escrow account.')) {
      return;
    }

    try {
      setError(null);
      await api.post(`/api/admin/projects/${projectId}/complete`);
      
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchData();
      }, 2000);
    } catch (error: any) {
      console.error('Error completing project:', error);
      setError(String(error?.response?.data?.error || 'Failed to complete project'));
    }
  };

  const handleRefundMilestone = async (phaseNumber: number) => {
    const milestone = milestones.find(m => m.number === phaseNumber);
    const amount = milestone ? `$${(milestone.amountCents / 100).toFixed(2)}` : '';
    
    if (!confirm(`Are you sure you want to REFUND ${amount} from the contractor?\n\nThis will:\n- Reverse the Stripe transfer\n- Return funds to escrow\n- Reset milestone status to "Waiting for homeowner approval"\n\nThis should only be used if:\n- Contractor missed something for this milestone\n- You overpaid them`)) {
      return;
    }

    try {
      setRefunding(`${phaseNumber}`);
      setError(null);
      
      console.log(`↩️ Refunding milestone ${phaseNumber}...`);
      const response = await api.post(`/api/admin/projects/${projectId}/phases/${phaseNumber}/refund`);
      
      console.log('✅ Refund response:', response);
      
      if (response.stripeRefundId) {
        alert(`✅ Refund processed successfully!\n\nAmount: ${response.amountFormatted || amount}\nStripe Refund ID: ${response.stripeRefundId}\n\nMilestone status reset to "Waiting for homeowner approval".`);
      } else {
        alert(`✅ Refund processed successfully! Amount: ${response.amountFormatted || amount}`);
      }
      
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchData(true); // Refresh with cache bypass
      }, 2000);
    } catch (error: any) {
      console.error('❌ Error refunding milestone:', error);
      const errorMessage = String(error?.response?.data?.error || error?.message || 'Failed to refund payment');
      const errorDetails = error?.response?.data?.details || '';
      const stripeError = error?.response?.data?.stripeError || '';

      let fullErrorMessage = errorMessage;
      if (errorDetails) fullErrorMessage += `\n\nDetails: ${errorDetails}`;
      if (stripeError) fullErrorMessage += `\n\nStripe Error: ${stripeError}`;

      setError(errorMessage);
      alert(`❌ Failed to refund payment:\n\n${fullErrorMessage}`);
    } finally {
      setRefunding(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading milestones...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: '#dc2626' }}>Project not found</p>
        <button onClick={() => navigate('/admin/projects')} style={{ marginTop: '16px' }}>
          Back to Projects
        </button>
      </div>
    );
  }

  const allMilestonesReleased = milestones.length > 0 && milestones.every(m => m.status === 'RELEASED' || m.status === 'ADMIN_RELEASED' || m.status === 'COMPLETED');
  const escrowBalance = project.escrowAccount?.currentBalance || 0;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <button 
          onClick={() => onNavigate?.('admin-projects-list') || navigate('/admin/projects')}
          style={{ marginBottom: '16px', padding: '8px 16px', cursor: 'pointer', borderRadius: '6px', border: '1px solid #d1d5db' }}
        >
          ← Back to Projects
        </button>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>
          Milestone Management
        </h1>
        {project.status === 'COMPLETED' && (
          <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '15px', fontWeight: 600, color: '#16a34a' }}>
            Project Completed
          </p>
        )}
        <p style={{ color: '#6b7280', marginTop: project.status === 'COMPLETED' ? '4px' : '8px' }}>
          Project: {project.address} ({project.status})
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div style={{ 
          background: '#d1fae5', 
          border: '1px solid #86efac', 
          borderRadius: '8px', 
          padding: '16px',
          marginBottom: '24px',
          color: '#065f46'
        }}>
          <strong>✓ Success!</strong> Action completed successfully.
        </div>
      )}

      {error && (
        <div style={{ 
          background: '#fee2e2', 
          border: '1px solid #fecaca', 
          borderRadius: '8px', 
          padding: '16px',
          marginBottom: '24px',
          color: '#dc2626'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Escrow Balance Card */}
      <div style={{ 
        background: '#f0f9ff', 
        border: '1px solid #bae6fd', 
        borderRadius: '8px', 
        padding: '24px',
        marginBottom: '32px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Escrow Balance</h3>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280' }}>Available for milestone releases</p>
          </div>
          <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#1e40af' }}>
            ${(escrowBalance / 100).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ marginBottom: '32px', display: 'flex', gap: '12px' }}>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '12px 24px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          + Create Milestone
        </button>
        {allMilestonesReleased && project.status !== 'COMPLETED' && (
          <button
            onClick={handleCompleteProject}
            style={{
              background: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            ✓ Mark Project Complete
          </button>
        )}
      </div>

      {/* Milestones List */}
      {milestones.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          background: 'white'
        }}>
          <p style={{ color: '#6b7280', fontSize: '18px' }}>No milestones yet</p>
          <p style={{ color: '#9ca3af', marginTop: '8px' }}>Create your first milestone to get started</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {milestones.map((milestone) => {
            const isEditing = editingPhase === milestone.number;
            
            return (
              <div 
                key={milestone.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '24px',
                  background: 'white'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                            Milestone Name
                          </label>
                          <input
                            type="text"
                            value={editPhaseName}
                            onChange={(e) => setEditPhaseName(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                            Amount ($) - Percent will auto-adjust
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={editPhaseAmount}
                            onChange={(e) => setEditPhaseAmount(e.target.value)}
                            style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleSavePhase(milestone.number)}
                            style={{
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setEditingPhase(null);
                              setEditPhaseName('');
                              setEditPhaseAmount('');
                            }}
                            style={{
                              background: '#6b7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                            Milestone {milestone.number}: {milestone.name}
                        </h3>
                        <div style={{ marginTop: '12px', display: 'flex', gap: '24px', color: '#6b7280' }}>
                          <span>Amount: <strong style={{ color: '#111827' }}>${(milestone.amountCents / 100).toFixed(2)}</strong></span>
                          <span>Percent: <strong style={{ color: '#111827' }}>{milestone.percent}%</strong></span>
                        </div>
                        <div style={{ marginTop: '12px' }}>
                          <span style={{
                            padding: '6px 16px',
                            borderRadius: '20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            background: (milestone.status === 'RELEASED' || milestone.status === 'ADMIN_RELEASED') ? '#d1fae5' :
                                       milestone.status === 'HOMEOWNER_APPROVED' ? '#dbeafe' :
                                       milestone.status === 'CONTRACTOR_SUBMITTED' ? '#fef3c7' :
                                       milestone.status === 'COMPLETED' ? '#dcfce7' :
                                       '#f3f4f6',
                            color: (milestone.status === 'RELEASED' || milestone.status === 'ADMIN_RELEASED') ? '#065f46' :
                                  milestone.status === 'HOMEOWNER_APPROVED' ? '#1e40af' :
                                  milestone.status === 'CONTRACTOR_SUBMITTED' ? '#92400e' :
                                  milestone.status === 'COMPLETED' ? '#166534' :
                                  '#6b7280'
                          }}>
                            {milestone.status === 'ADMIN_RELEASED' || milestone.status === 'RELEASED' ? '💰 Payment Released' :
                             milestone.status === 'HOMEOWNER_APPROVED' ? '✅ Evidence Approved' :
                             milestone.status === 'CONTRACTOR_SUBMITTED' ? '📋 Contractor Submitted' :
                             milestone.status === 'PENDING' ? '⏳ Pending' :
                             milestone.status === 'COMPLETED' ? '✅ Completed' :
                             milestone.status.replace('_', ' ')}
                          </span>
                        </div>

                        {/* Evidence section */}
                        {(() => {
                          const evidenceList = milestone.milestone?.evidence ?? [];
                          if (evidenceList.length === 0) return null;
                          const latestEvidence = evidenceList[0];
                          return (
                            <div style={{ marginTop: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <strong style={{ fontSize: '14px' }}>Contractor Evidence</strong>
                                <span style={{
                                  padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
                                  background: latestEvidence.status === 'APPROVED' ? '#dcfce7' : latestEvidence.status === 'REJECTED' ? '#fee2e2' : '#fef9c3',
                                  color: latestEvidence.status === 'APPROVED' ? '#166534' : latestEvidence.status === 'REJECTED' ? '#991b1b' : '#713f12',
                                }}>
                                  {latestEvidence.status === 'APPROVED' ? 'Approved' : latestEvidence.status === 'REJECTED' ? 'Rejected' : 'Pending Review'}
                                </span>
                                <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                                  Submitted {new Date(latestEvidence.submittedAt).toLocaleDateString()}
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {latestEvidence.fileUrls.map((f, i) => (
                                  f.url ? (
                                    <a
                                      key={f.key}
                                      href={f.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                                        padding: '6px 12px', background: 'white', border: '1px solid #d1d5db',
                                        borderRadius: '6px', fontSize: '13px', color: '#2563eb',
                                        textDecoration: 'none', fontWeight: '500',
                                      }}
                                    >
                                      📎 File {i + 1}
                                    </a>
                                  ) : (
                                    <span key={f.key} style={{ fontSize: '13px', color: '#94a3b8', padding: '6px 12px' }}>
                                      File {i + 1} (unavailable)
                                    </span>
                                  )
                                ))}
                              </div>
                              {latestEvidence.status === 'PENDING_APPROVAL' && (
                                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                  <button
                                    onClick={() => handleApproveEvidence(milestone.number, true)}
                                    disabled={approvingEvidence === `${milestone.number}`}
                                    style={{
                                      background: '#10b981', color: 'white', border: 'none', borderRadius: '6px',
                                      padding: '8px 16px', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                                      opacity: approvingEvidence === `${milestone.number}` ? 0.7 : 1,
                                    }}
                                  >
                                    {approvingEvidence === `${milestone.number}` ? 'Processing...' : '✅ Approve Evidence'}
                                  </button>
                                  <button
                                    onClick={() => handleApproveEvidence(milestone.number, false)}
                                    disabled={approvingEvidence === `${milestone.number}`}
                                    style={{
                                      background: 'white', color: '#dc2626', border: '1px solid #dc2626', borderRadius: '6px',
                                      padding: '8px 16px', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                                      opacity: approvingEvidence === `${milestone.number}` ? 0.7 : 1,
                                    }}
                                  >
                                    ❌ Reject Evidence
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '16px' }}>
                    {!isEditing && (
                      <>
                        <button
                          onClick={() => handleEditPhase(milestone)}
                          style={{
                            background: '#6366f1',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '14px'
                          }}
                        >
                          ✏️ Edit
                        </button>
                        {(milestone.status !== 'RELEASED' && milestone.status !== 'ADMIN_RELEASED') && (
                          <button
                            onClick={() => handleDeletePhase(milestone.number, milestone.name)}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              fontSize: '14px'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        )}
                        {/* Release button: show when evidence approved, homeowner approved, or contractor submitted */}
                        {(() => {
                          const mStatus = milestone.milestone?.status;
                          const canRelease = milestone.status === 'HOMEOWNER_APPROVED'
                            || mStatus === 'APPROVED'
                            || milestone.status === 'CONTRACTOR_SUBMITTED';
                          if (!canRelease) return null;
                          const isReady = milestone.status === 'HOMEOWNER_APPROVED' || mStatus === 'APPROVED';
                          return (
                            <button
                              onClick={() => handleReleaseMilestone(milestone.number)}
                              disabled={releasing === `${milestone.number}`}
                              style={{
                                background: isReady ? '#10b981' : '#3b82f6',
                                color: 'white', border: 'none', borderRadius: '6px',
                                padding: '8px 16px',
                                cursor: releasing === `${milestone.number}` ? 'not-allowed' : 'pointer',
                                fontWeight: '600', fontSize: '14px',
                                opacity: releasing === `${milestone.number}` ? 0.7 : 1,
                              }}
                              title={isReady ? 'Release payment to contractor' : 'Release payment (evidence pending approval)'}
                            >
                              {releasing === `${milestone.number}` ? 'Releasing...' : '💰 Release Payment'}
                            </button>
                          );
                        })()}
                        {(milestone.status === 'ADMIN_RELEASED' || milestone.status === 'RELEASED') && (
                          <button
                            onClick={() => handleRefundMilestone(milestone.number)}
                            disabled={refunding === `${milestone.number}`}
                            style={{
                              background: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: refunding === `${milestone.number}` ? 'not-allowed' : 'pointer',
                              fontWeight: '600',
                              fontSize: '14px',
                              opacity: refunding === `${milestone.number}` ? 0.7 : 1
                            }}
                          >
                            {refunding === `${milestone.number}` ? 'Refunding...' : '↩️ Request Refund'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Milestone Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowCreateModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '32px',
            width: '90%',
            maxWidth: '500px'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 24px 0' }}>Create Milestone</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  Milestone Name
                </label>
                <input
                  type="text"
                  value={newMilestone.name}
                  onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                  placeholder="e.g., Phase 1: Demolition"
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  Milestone Percentage of Total
                </label>
                <input
                  type="number"
                  value={newMilestone.percent}
                  onChange={(e) => setNewMilestone({ ...newMilestone, percent: e.target.value })}
                  placeholder="e.g., 30"
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  Amount ($)
                </label>
                <input
                  type="number"
                  value={newMilestone.amount}
                  onChange={(e) => setNewMilestone({ ...newMilestone, amount: e.target.value })}
                  placeholder="e.g., 5000.00"
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewMilestone({ name: '', percent: '', amount: '' });
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: 'white',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateMilestone}
                  style={{
                    flex: 1,
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '12px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Create Milestone
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMilestoneManagement;
// CB:END admin-milestone-management-production
